/**
 * THE MIRROR OF TRUTH 🪞 - Divine Business Audit
 * ═══════════════════════════════════════════════════════════════
 * 
 * "Step into the Chamber. Let the gods judge your business."
 * 
 * An immersive experience where users input their metrics and
 * watch the gods REACT with their personalities — not just
 * dry benchmark comparisons, but dramatic strategic interventions.
 * 
 * Features:
 * - Dramatic metric input with visual feedback
 * - Real-time god reactions as you enter data
 * - Personalized benchmark comparisons
 * - God recommendations based on weak areas
 * - Context injection into War Council debates
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, ChevronRight, ChevronLeft, Sparkles, AlertTriangle,
  TrendingUp, TrendingDown, Minus, Check, Loader2,
  Brain, Target, Zap, DollarSign, Users, BarChart3,
  RefreshCw, Save, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// GOD DEFINITIONS FOR REACTIONS
// ═══════════════════════════════════════════════════════════════

const REACTING_GODS = {
  plutus: {
    name: 'Plutus',
    emoji: '💰',
    title: 'God of Wealth',
    color: 'from-green-500 to-emerald-600',
    textColor: 'text-green-400',
    focus: ['cac', 'ltv', 'ltv_cac_ratio', 'cac_payback_months', 'burn_rate', 'gross_margin', 'runway_months', 'rule_of_40'],
    reactions: {
      outperforming: [
        "The treasury overflows. This metric is a weapon.",
        "Now THIS is how you build an empire. Exemplary.",
        "Profitable efficiency. The investors will kneel."
      ],
      at_benchmark: [
        "Acceptable. Not bleeding, not winning. Room to optimize.",
        "You're playing it safe. Safe doesn't build empires.",
        "The median is for the mediocre. Push harder."
      ],
      needs_improvement: [
        "The money bleeds here. This wound must be closed.",
        "This inefficiency is costing you runway. Fix it or fold.",
        "Every day this persists, competitors gain ground."
      ]
    }
  },
  eris: {
    name: 'Eris',
    emoji: '⚔️',
    title: 'Goddess of Discord',
    color: 'from-red-600 to-pink-600',
    textColor: 'text-red-400',
    focus: ['monthly_churn', 'annual_churn', 'nps', 'very_disappointed_pct'],
    reactions: {
      outperforming: [
        "Impressive... but have you stress-tested this? What breaks first?",
        "Strong number. But what are you NOT measuring that could destroy you?",
        "Success breeds complacency. Don't let this make you soft."
      ],
      at_benchmark: [
        "Average. The question is: do you KNOW why it's average?",
        "You're blending in with the crowd. Is that the strategy?",
        "Benchmark means others have already won here. What's YOUR edge?"
      ],
      needs_improvement: [
        "Here's your real problem. Everyone else is just distraction.",
        "This is the crack in the foundation. Ignore it at your peril.",
        "But have you asked WHY? The symptom isn't the disease."
      ]
    }
  },
  themis: {
    name: 'Themis',
    emoji: '⚖️',
    title: 'Titan of Divine Law',
    color: 'from-blue-600 to-cyan-600',
    textColor: 'text-blue-400',
    focus: ['website_conversion_rate', 'trial_to_paid_rate', 'email_open_rate', 'email_ctr', 'mql_to_sql_rate'],
    reactions: {
      outperforming: [
        "The data confirms excellence. Top quartile performance verified.",
        "Statistically significant outperformance. Document what's working.",
        "Above the 90th percentile. This is replicable if you understand why."
      ],
      at_benchmark: [
        "Within expected range. The data suggests optimization potential.",
        "Benchmark adherence confirmed. Now isolate the variables for improvement.",
        "Standard performance. What hypothesis would you test to break out?"
      ],
      needs_improvement: [
        "The numbers don't lie. This is a 2-sigma deviation from healthy.",
        "Statistically concerning. The benchmark exists for a reason.",
        "Data suggests intervention required. What's your remediation hypothesis?"
      ]
    }
  },
  nike: {
    name: 'Nike',
    emoji: '🏆',
    title: 'Goddess of Victory',
    color: 'from-yellow-500 to-orange-600',
    textColor: 'text-yellow-400',
    focus: ['monthly_growth_rate', 'annual_growth_rate', 'arr', 'mrr', 'win_rate'],
    reactions: {
      outperforming: [
        "DOMINANT. This is how champions are made. PUSH HARDER.",
        "Victory favors the bold — and you're being bold here. Scale it!",
        "Top performer territory. Now pour fuel on this fire!"
      ],
      at_benchmark: [
        "Average growth is for average companies. You're better than this.",
        "The competition isn't resting. Why are you?",
        "Meeting the bar isn't winning. Raise it or get left behind."
      ],
      needs_improvement: [
        "This is where empires fall. Attack this weakness NOW.",
        "Your competitors see this gap. They're already exploiting it.",
        "No excuses. Fix this or concede the market."
      ]
    }
  },
  hestia: {
    name: 'Hestia',
    emoji: '🏠',
    title: 'Goddess of the Hearth',
    color: 'from-orange-400 to-amber-500',
    textColor: 'text-orange-300',
    focus: ['net_revenue_retention', 'gross_revenue_retention', 'nps', 'activation_rate'],
    reactions: {
      outperforming: [
        "Your customers are happy and staying. This is the foundation of everything.",
        "Community strength confirmed. They trust you. Honor that trust.",
        "Retention is love made metric. You're doing something right."
      ],
      at_benchmark: [
        "Stable, but are your customers truly delighted? Or just... not leaving yet?",
        "The hearth is warm, but not roaring. What would make them advocates?",
        "Satisfaction isn't passion. What would make them LOVE you?"
      ],
      needs_improvement: [
        "Your customers are leaving. Before you acquire more, understand why.",
        "A leaky bucket serves no one. The community is hurting.",
        "This isn't a metric problem — it's a trust problem. Listen to them."
      ]
    }
  },
  metis: {
    name: 'Metis',
    emoji: '🧠',
    title: 'Titan of Wisdom',
    color: 'from-indigo-500 to-purple-600',
    textColor: 'text-indigo-400',
    focus: ['rule_of_40', 'sales_cycle_days', 'feature_adoption_rate'],
    reactions: {
      outperforming: [
        "A strong position. But think ten moves ahead — how do you defend this?",
        "Excellence today means expectation tomorrow. What's the moat?",
        "The wise consolidate gains. Document, systematize, fortify."
      ],
      at_benchmark: [
        "Acceptable positioning. But the market shifts. What do you see coming?",
        "The middle path is safe but vulnerable. Choose a direction.",
        "Balance is temporary. What disruption could change everything?"
      ],
      needs_improvement: [
        "This weakness has deeper roots. Trace the chain of causation.",
        "A strategic gap. Fix the symptom, but investigate the system.",
        "The pattern reveals itself. This connects to more than you see."
      ]
    }
  }
};

// Metric categories for the input wizard
const METRIC_STEPS = [
  {
    id: 'acquisition',
    title: 'Customer Acquisition',
    icon: DollarSign,
    description: 'How efficiently do you acquire customers?',
    color: 'from-green-500 to-emerald-600',
    metrics: [
      { key: 'cac', label: 'Customer Acquisition Cost', unit: '$', placeholder: '150', hint: 'Average cost to acquire one customer' },
      { key: 'ltv', label: 'Customer Lifetime Value', unit: '$', placeholder: '450', hint: 'Average revenue per customer lifetime' },
      { key: 'cac_payback_months', label: 'CAC Payback Period', unit: 'months', placeholder: '15', hint: 'Months to recover acquisition cost' }
    ]
  },
  {
    id: 'retention',
    title: 'Retention & Churn',
    icon: Users,
    description: 'How well do you keep customers?',
    color: 'from-red-500 to-pink-600',
    metrics: [
      { key: 'monthly_churn', label: 'Monthly Churn Rate', unit: '%', placeholder: '5', hint: 'Percentage of customers lost per month' },
      { key: 'net_revenue_retention', label: 'Net Revenue Retention', unit: '%', placeholder: '105', hint: 'Revenue from existing customers (100%+ means expansion)' }
    ]
  },
  {
    id: 'conversion',
    title: 'Conversion Metrics',
    icon: Target,
    description: 'How well do you convert interest to action?',
    color: 'from-blue-500 to-cyan-600',
    metrics: [
      { key: 'website_conversion_rate', label: 'Website Conversion Rate', unit: '%', placeholder: '3.5', hint: 'Visitors who take desired action' },
      { key: 'trial_to_paid_rate', label: 'Trial to Paid Conversion', unit: '%', placeholder: '18', hint: 'Free trials that convert to paid' }
    ]
  },
  {
    id: 'growth',
    title: 'Growth Metrics',
    icon: TrendingUp,
    description: 'How fast are you growing?',
    color: 'from-yellow-500 to-orange-600',
    metrics: [
      { key: 'annual_growth_rate', label: 'Annual Revenue Growth', unit: '%', placeholder: '30', hint: 'Year-over-year revenue growth' },
      { key: 'arr', label: 'Annual Recurring Revenue', unit: '$', placeholder: '500000', hint: 'Total annualized recurring revenue' }
    ]
  },
  {
    id: 'financial',
    title: 'Financial Health',
    icon: BarChart3,
    description: 'How sustainable is your business?',
    color: 'from-purple-500 to-indigo-600',
    metrics: [
      { key: 'gross_margin', label: 'Gross Margin', unit: '%', placeholder: '75', hint: 'Revenue minus cost of goods sold' },
      { key: 'burn_rate', label: 'Monthly Burn Rate', unit: '$', placeholder: '50000', hint: 'Net cash spent per month' },
      { key: 'runway_months', label: 'Runway', unit: 'months', placeholder: '18', hint: 'Months until cash runs out' }
    ]
  }
];

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const getReactionForMetric = (metricKey, status) => {
  // Find which god cares about this metric
  for (const [godId, god] of Object.entries(REACTING_GODS)) {
    if (god.focus.includes(metricKey)) {
      const reactions = god.reactions[status] || god.reactions.at_benchmark;
      const reaction = reactions[Math.floor(Math.random() * reactions.length)];
      return { god, godId, reaction };
    }
  }
  // Default to Themis for uncategorized metrics
  const reactions = REACTING_GODS.themis.reactions[status];
  return { 
    god: REACTING_GODS.themis, 
    godId: 'themis',
    reaction: reactions[Math.floor(Math.random() * reactions.length)]
  };
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'outperforming':
    case 'above_range':
      return <TrendingUp className="w-4 h-4 text-green-400" />;
    case 'needs_improvement':
    case 'below_range':
      return <TrendingDown className="w-4 h-4 text-red-400" />;
    default:
      return <Minus className="w-4 h-4 text-yellow-400" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'outperforming':
    case 'above_range':
      return 'text-green-400 bg-green-500/10 border-green-500/30';
    case 'needs_improvement':
    case 'below_range':
      return 'text-red-400 bg-red-500/10 border-red-500/30';
    default:
      return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
  }
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

// God reaction bubble with animation
const GodReaction = ({ god, godId, reaction, metric, comparison, isNew }) => {
  const status = comparison?.status || 'at_benchmark';
  
  return (
    <div className={`flex gap-3 ${isNew ? 'animate-fade-in' : ''}`}>
      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${god.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
        <span className="text-lg">{god.emoji}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`font-bold text-sm ${god.textColor}`}>{god.name}</span>
          <span className="text-xs text-gray-500">{god.title}</span>
        </div>
        <div className={`rounded-xl rounded-tl-sm px-4 py-3 ${god.textColor.replace('text-', 'bg-').replace('400', '500/10')} border ${god.textColor.replace('text-', 'border-').replace('400', '500/30')}`}>
          <p className="text-sm text-gray-200 italic">"{reaction}"</p>
          {comparison && (
            <div className="mt-2 pt-2 border-t border-gray-700/50 flex items-center gap-3 text-xs">
              {getStatusIcon(status)}
              <span className="text-gray-400">
                Your {comparison.metric_name}: <span className="text-white font-medium">{comparison.user_value}{comparison.unit}</span>
                {comparison.benchmark_value && (
                  <> vs benchmark: <span className="text-white font-medium">{comparison.benchmark_value}{comparison.unit}</span></>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Metric input card
const MetricInput = ({ metric, value, onChange, comparison }) => {
  const status = comparison?.status;
  const statusClass = status ? getStatusColor(status) : 'border-gray-700';
  
  return (
    <div className={`bg-gray-800/50 rounded-xl p-4 border ${statusClass} transition-all`}>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {metric.label}
      </label>
      <p className="text-xs text-gray-500 mb-3">{metric.hint}</p>
      <div className="flex items-center gap-2">
        {metric.unit === '$' && <span className="text-gray-500">$</span>}
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(metric.key, e.target.value)}
          placeholder={metric.placeholder}
          className="flex-1 bg-gray-900/50 border border-gray-700 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none transition-all"
        />
        {metric.unit !== '$' && <span className="text-gray-500 text-sm">{metric.unit}</span>}
      </div>
      {comparison && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          {getStatusIcon(comparison.status)}
          <span className="text-gray-400">{comparison.interpretation}</span>
        </div>
      )}
    </div>
  );
};

// Progress indicator
const StepProgress = ({ steps, currentStep, completedSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {steps.map((step, index) => {
      const isCompleted = completedSteps.includes(step.id);
      const isCurrent = index === currentStep;
      const Icon = step.icon;
      
      return (
        <React.Fragment key={step.id}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              isCurrent 
                ? `bg-gradient-to-br ${step.color} shadow-lg` 
                : isCompleted 
                  ? 'bg-green-500/20 border border-green-500/50' 
                  : 'bg-gray-800 border border-gray-700'
            }`}
          >
            {isCompleted ? (
              <Check className="w-5 h-5 text-green-400" />
            ) : (
              <Icon className={`w-5 h-5 ${isCurrent ? 'text-white' : 'text-gray-500'}`} />
            )}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 h-0.5 ${isCompleted ? 'bg-green-500/50' : 'bg-gray-700'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// Summary card for the results screen
const MetricSummaryCard = ({ comparison, onSelect }) => {
  const status = comparison.status;
  const statusClass = getStatusColor(status);
  
  return (
    <div 
      className={`p-4 rounded-xl border ${statusClass} cursor-pointer hover:scale-[1.02] transition-all`}
      onClick={() => onSelect(comparison)}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-white">{comparison.metric_name}</span>
        {getStatusIcon(status)}
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-400">Yours: <span className="text-white">{comparison.user_value}{comparison.unit}</span></span>
        {comparison.benchmark_value && (
          <span className="text-gray-500">Benchmark: {comparison.benchmark_value}{comparison.unit}</span>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const MirrorOfTruth = ({ isOpen, onClose, onMetricsSaved }) => {
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [metrics, setMetrics] = useState({});
  const [comparisons, setComparisons] = useState({});
  const [godReactions, setGodReactions] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [hasExistingMetrics, setHasExistingMetrics] = useState(false);
  
  const reactionsEndRef = useRef(null);
  
  // Auto-scroll to latest reaction
  useEffect(() => {
    reactionsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [godReactions]);
  
  // Load existing metrics on open
  useEffect(() => {
    if (isOpen) {
      loadExistingMetrics();
    }
  }, [isOpen]);
  
  const loadExistingMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/metrics/my-metrics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_metrics) {
          setMetrics(data.metrics);
          setHasExistingMetrics(true);
          // Generate comparisons for existing metrics
          data.comparisons?.forEach(comp => {
            setComparisons(prev => ({ ...prev, [comp.metric]: comp }));
          });
        }
      }
    } catch (err) {
      console.error('Failed to load existing metrics:', err);
    }
  };
  
  // Handle metric input change
  const handleMetricChange = async (key, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    setMetrics(prev => ({ ...prev, [key]: numValue }));
    
    // If we have a value, get real-time comparison
    if (numValue !== null && !isNaN(numValue)) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/api/metrics/compare?metric_key=${key}&value=${numValue}`,
          {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          const comparison = data.comparison;
          
          setComparisons(prev => ({ ...prev, [key]: comparison }));
          
          // Generate god reaction
          const { god, godId, reaction } = getReactionForMetric(key, comparison.status);
          
          // Add reaction (replace if same metric)
          setGodReactions(prev => {
            const filtered = prev.filter(r => r.metricKey !== key);
            return [...filtered, { 
              god, 
              godId, 
              reaction, 
              comparison,
              metricKey: key,
              timestamp: Date.now()
            }];
          });
        }
      } catch (err) {
        console.error('Comparison error:', err);
      }
    }
  };
  
  // Navigate steps
  const nextStep = () => {
    const currentStepData = METRIC_STEPS[currentStep];
    const hasAnyValue = currentStepData.metrics.some(m => metrics[m.key]);
    
    if (hasAnyValue) {
      setCompletedSteps(prev => [...new Set([...prev, currentStepData.id])]);
    }
    
    if (currentStep < METRIC_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Run full audit
      runAudit();
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Run the full benchmark audit
  const runAudit = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Filter out null/undefined values
      const validMetrics = Object.fromEntries(
        Object.entries(metrics).filter(([_, v]) => v !== null && v !== undefined && !isNaN(v))
      );
      
      if (Object.keys(validMetrics).length === 0) {
        alert('Please enter at least one metric');
        setIsLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/metrics/benchmark-audit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          metrics: validMetrics,
          context: { arr: metrics.arr }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditResult(data.audit);
        setShowResults(true);
        
        // Generate dramatic reactions for the results
        const resultReactions = [];
        
        // Strengths reaction
        if (data.audit.strengths?.length > 0) {
          const strength = data.audit.strengths[0];
          const { god, godId, reaction } = getReactionForMetric(strength.metric, 'outperforming');
          resultReactions.push({ god, godId, reaction, comparison: strength, metricKey: strength.metric });
        }
        
        // Weakness reaction
        if (data.audit.weaknesses?.length > 0) {
          const weakness = data.audit.weaknesses[0];
          const { god, godId, reaction } = getReactionForMetric(weakness.metric, 'needs_improvement');
          resultReactions.push({ god, godId, reaction, comparison: weakness, metricKey: weakness.metric });
        }
        
        setGodReactions(resultReactions);
      }
    } catch (err) {
      console.error('Audit error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save metrics to user profile
  const saveMetrics = async () => {
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const validMetrics = Object.fromEntries(
        Object.entries(metrics).filter(([_, v]) => v !== null && v !== undefined && !isNaN(v))
      );
      
      const response = await fetch(`${API_URL}/api/metrics/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          metrics: validMetrics,
          context: { arr: metrics.arr }
        })
      });
      
      if (response.ok) {
        if (onMetricsSaved) onMetricsSaved(validMetrics);
        // Show success feedback
        alert('🪞 Your metrics have been saved. The gods will remember.');
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Reset and start over
  const resetMirror = () => {
    setCurrentStep(0);
    setMetrics({});
    setComparisons({});
    setGodReactions([]);
    setCompletedSteps([]);
    setAuditResult(null);
    setShowResults(false);
  };
  
  if (!isOpen) return null;
  
  const currentStepData = METRIC_STEPS[currentStep];
  const filledMetricsCount = Object.values(metrics).filter(v => v !== null && v !== undefined && !isNaN(v)).length;
  
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 via-gray-900 to-purple-900/20 border border-purple-500/30 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl shadow-purple-500/10">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between bg-black/30">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <span className="text-2xl">🪞</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">The Mirror of Truth</h2>
              <p className="text-sm text-gray-400">Let the gods judge your business</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Input */}
          <div className="flex-1 p-6 overflow-y-auto border-r border-gray-800">
            {!showResults ? (
              <>
                {/* Progress */}
                <StepProgress 
                  steps={METRIC_STEPS} 
                  currentStep={currentStep} 
                  completedSteps={completedSteps}
                />
                
                {/* Current Step */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${currentStepData.color} flex items-center justify-center`}>
                      <currentStepData.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{currentStepData.title}</h3>
                      <p className="text-sm text-gray-400">{currentStepData.description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Metric Inputs */}
                <div className="space-y-4 mb-6">
                  {currentStepData.metrics.map(metric => (
                    <MetricInput
                      key={metric.key}
                      metric={metric}
                      value={metrics[metric.key]}
                      onChange={handleMetricChange}
                      comparison={comparisons[metric.key]}
                    />
                  ))}
                </div>
                
                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                  
                  <div className="text-sm text-gray-500">
                    {filledMetricsCount} metrics entered
                  </div>
                  
                  <button
                    onClick={nextStep}
                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg flex items-center gap-2 transition-all shadow-lg shadow-purple-500/20"
                  >
                    {currentStep === METRIC_STEPS.length - 1 ? (
                      <>
                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                        Run Divine Audit
                      </>
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Results View */
              <div className="animate-fade-in">
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
                    auditResult?.health_score >= 70 
                      ? 'bg-green-500/20 border border-green-500/50' 
                      : auditResult?.health_score >= 50 
                        ? 'bg-yellow-500/20 border border-yellow-500/50'
                        : 'bg-red-500/20 border border-red-500/50'
                  }`}>
                    <span className="text-4xl font-bold text-white">{Math.round(auditResult?.health_score || 0)}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Divine Audit Complete</h3>
                  <p className="text-gray-400">
                    {auditResult?.total_metrics} metrics analyzed • 
                    <span className="text-green-400"> {auditResult?.summary?.outperforming} strong</span> • 
                    <span className="text-yellow-400"> {auditResult?.summary?.at_benchmark} average</span> • 
                    <span className="text-red-400"> {auditResult?.summary?.needs_improvement} need work</span>
                  </p>
                </div>
                
                {/* Strengths */}
                {auditResult?.strengths?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-green-400 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Your Strengths
                    </h4>
                    <div className="grid gap-3">
                      {auditResult.strengths.map((s, i) => (
                        <MetricSummaryCard key={i} comparison={s} onSelect={() => {}} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Weaknesses */}
                {auditResult?.weaknesses?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-red-400 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Priority Improvements
                    </h4>
                    <div className="grid gap-3">
                      {auditResult.weaknesses.map((w, i) => (
                        <MetricSummaryCard key={i} comparison={w} onSelect={() => {}} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Recommended Gods */}
                {auditResult?.recommended_gods?.length > 0 && (
                  <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                    <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                      <Brain className="w-4 h-4" /> Summon These Gods
                    </h4>
                    <div className="space-y-2">
                      {auditResult.recommended_gods.map((rec, i) => {
                        const god = REACTING_GODS[rec.god];
                        return (
                          <div key={i} className="flex items-center gap-3 text-sm">
                            <span className="text-lg">{god?.emoji || '🤖'}</span>
                            <span className={`font-medium ${god?.textColor || 'text-gray-300'}`}>
                              {god?.name || rec.god}
                            </span>
                            <span className="text-gray-500">—</span>
                            <span className="text-gray-400">{rec.reason}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={resetMirror}
                    className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Start Over
                  </button>
                  <button
                    onClick={saveMetrics}
                    disabled={isSaving}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/20"
                  >
                    {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Save to Profile
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 text-center mt-3">
                  Saved metrics will be used by the gods in War Council debates
                </p>
              </div>
            )}
          </div>
          
          {/* Right Panel - God Reactions */}
          <div className="w-[400px] p-6 overflow-y-auto bg-black/20">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-purple-400" />
              <h3 className="font-bold text-white">Divine Reactions</h3>
            </div>
            
            {godReactions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3 opacity-50">🪞</div>
                <p className="text-gray-500 text-sm">Enter your metrics and watch the gods react...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {godReactions.map((reaction, i) => (
                  <GodReaction
                    key={`${reaction.metricKey}-${reaction.timestamp}`}
                    god={reaction.god}
                    godId={reaction.godId}
                    reaction={reaction.reaction}
                    comparison={reaction.comparison}
                    isNew={i === godReactions.length - 1}
                  />
                ))}
                <div ref={reactionsEndRef} />
              </div>
            )}
          </div>
        </div>
        
        {/* CSS */}
        <style>{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in { animation: fade-in 0.4s ease-out; }
        `}</style>
      </div>
    </div>
  );
};

export default MirrorOfTruth;
